const UNDO_COMMAND_ID = 'univer.command.undo'
const REDO_COMMAND_ID = 'univer.command.redo'
const UNDO_ICON_CLASS = 'univerjs-icon-undo-single'
const REDO_ICON_CLASS = 'univerjs-icon-redo-single'

let installed = false
let suppressClickUntil = 0
let lastPointerButton: HTMLButtonElement | null = null
let executing = false

type UndoRedoAction = 'undo' | 'redo'

const getUniverAPI = () => {
  if (typeof window === 'undefined') return null
  return (window as any).__JIUDA_UNIVER_API__ || (window as any).univerAPI || null
}

const findUndoRedoButton = (target: EventTarget | null): { action: UndoRedoAction; button: HTMLButtonElement } | null => {
  if (!(target instanceof Element)) return null

  const button = target.closest('button') as HTMLButtonElement | null
  if (!button || !button.closest('.univer-workbench-layout')) return null

  if (button.querySelector(`.${UNDO_ICON_CLASS}`)) return { action: 'undo', button }
  if (button.querySelector(`.${REDO_ICON_CLASS}`)) return { action: 'redo', button }
  return null
}

const isInsideUniverWorkbench = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false
  return !!target.closest('.univer-workbench-layout')
}

const stopEvent = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
}

const commitCurrentCellEditor = () => {
  const activeElement = document.activeElement as HTMLElement | null
  if (!activeElement || !activeElement.closest?.('.univer-workbench-layout')) return false

  const isEditor = activeElement instanceof HTMLInputElement
    || activeElement instanceof HTMLTextAreaElement
    || activeElement.isContentEditable
    || activeElement.classList.contains('univer-editor')
    || !!activeElement.closest('.univer-editor')

  try {
    // 先 blur，使仍在编辑器中的值进入命令历史栈，再执行撤销/重做。
    activeElement.blur()
    return isEditor
  } catch {
    return false
  }
}

const focusActiveWorkbook = (api: any) => {
  const workbook = api?.getActiveWorkbook?.()
  if (!workbook) return null

  try {
    const sheet = workbook.getActiveSheet?.()
    sheet?.activate?.()
    sheet?.getSelection?.()?.getActiveRange?.()?.activate?.()
  } catch {
    // 旧快照可能没有活动选区，不影响后续工作簿Facade聚焦兜底。
  }

  return workbook
}

const executeRootFacade = async (api: any, action: UndoRedoAction) => {
  const method = action === 'undo' ? api?.undo : api?.redo
  if (typeof method !== 'function') return false

  try {
    return await Promise.resolve(method.call(api)) === true
  } catch (error) {
    console.warn(`[JIUDA] Univer公开${action === 'undo' ? '撤销' : '重做'}API执行失败，尝试工作簿兜底:`, error)
    return false
  }
}

const executeWorkbookFacade = (workbook: any, action: UndoRedoAction) => {
  const method = action === 'undo' ? workbook?.undo : workbook?.redo
  if (typeof method !== 'function') return false

  try {
    // Univer 0.6.x的FWorkbook方法会先聚焦对应unit，再同步执行命令。
    method.call(workbook)
    return true
  } catch (error) {
    console.warn(`[JIUDA] 工作簿${action === 'undo' ? '撤销' : '重做'}兜底执行失败:`, error)
    return false
  }
}

const executeCommandFallback = async (api: any, action: UndoRedoAction) => {
  const commandId = action === 'undo' ? UNDO_COMMAND_ID : REDO_COMMAND_ID
  const execute = typeof api?.executeCommand === 'function'
    ? api.executeCommand.bind(api)
    : api?._commandService?.executeCommand?.bind(api._commandService)

  if (!execute) return false

  try {
    return await Promise.resolve(execute(commandId)) !== false
  } catch (error) {
    console.warn(`[JIUDA] ${action === 'undo' ? '撤销' : '重做'}命令兜底执行失败:`, error)
    return false
  }
}

const executeUndoRedo = async (action: UndoRedoAction, retryAfterCommit = false) => {
  if (executing) return false

  const api = getUniverAPI()
  if (!api) return false

  executing = true
  try {
    const workbook = focusActiveWorkbook(api)

    // 官方FUniver接口返回真实boolean结果，优先使用。
    if (await executeRootFacade(api, action)) return true

    // 导入后重建实例时，全局焦点可能仍停留在旧unit；FWorkbook会重新focusUnit。
    if (executeWorkbookFacade(workbook, action)) return true

    if (await executeCommandFallback(api, action)) return true

    if (retryAfterCommit) {
      await new Promise(resolve => window.setTimeout(resolve, 40))
      focusActiveWorkbook(api)
      return await executeRootFacade(api, action)
        || executeWorkbookFacade(workbook, action)
        || await executeCommandFallback(api, action)
    }

    return false
  } finally {
    executing = false
  }
}

const scheduleUndoRedo = (action: UndoRedoAction) => {
  const committed = commitCurrentCellEditor()
  window.setTimeout(() => {
    void executeUndoRedo(action, committed)
  }, committed ? 16 : 0)
}

const onPointerDown = (event: PointerEvent) => {
  const match = findUndoRedoButton(event.target)
  if (!match) return

  stopEvent(event)
  suppressClickUntil = Date.now() + 500
  lastPointerButton = match.button
  scheduleUndoRedo(match.action)
}

const onClick = (event: MouseEvent) => {
  const match = findUndoRedoButton(event.target)
  if (!match) return

  if (Date.now() <= suppressClickUntil && match.button === lastPointerButton) {
    stopEvent(event)
    return
  }

  stopEvent(event)
  scheduleUndoRedo(match.action)
}

const onKeyDown = (event: KeyboardEvent) => {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return

  const key = event.key.toLowerCase()
  const action: UndoRedoAction | null = key === 'z'
    ? (event.shiftKey ? 'redo' : 'undo')
    : key === 'y'
      ? 'redo'
      : null

  if (!action) return

  const activeElement = document.activeElement
  if (!isInsideUniverWorkbench(event.target) && !isInsideUniverWorkbench(activeElement)) return

  stopEvent(event)
  scheduleUndoRedo(action)
}

/**
 * 将工具栏按钮和快捷键始终路由到当前页面的Univer实例。
 */
export const installUniverUndoRedoPatch = () => {
  if (installed || typeof document === 'undefined') return
  installed = true

  document.addEventListener('pointerdown', onPointerDown, true)
  document.addEventListener('click', onClick, true)
  document.addEventListener('keydown', onKeyDown, true)
}
